import {Component, Inject, Input, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, ControlValueAccessor, FormControl, FormControlDirective, FormGroup} from '@angular/forms';
import {BaseComponent} from './base-component';
import {FloatLabelType, MatFormFieldAppearance} from '@angular/material/form-field';
import {ValidationError} from '../model/validation-error';
import {LabelWithParameters} from '../model/label-with-parameters';

@Component({
  template: '',
})
export class BaseFormComponent extends BaseComponent implements OnInit, ControlValueAccessor {
  @Input() id?: string;
  @Input() name?: string;
  @Input() className: string = '';
  @Input() floatLabel?: FloatLabelType;
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() formControlName?: string = '';
  @Input() formGroup: FormGroup;
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() isVisible: boolean = true;
  @Input() isReadonly: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() formGroupValidators?: string[];
  @ViewChild(FormControlDirective, {static: true}) formControlDirective: FormControlDirective;

  requiredAsterix: string;

  constructor(@Inject('ValidationErrorMap') protected errorMap: Record<string, ValidationError>) {
    super();
  }

  get control(): FormControl {
    return this.formGroup.controls[this.formControlName] as FormControl;
  }

  ngOnInit(): void {
    if (this.isReadonly) {
      this.control.disable();
    }

    this.requiredAsterix = this.getRequiredAsterix();
  }

  isControlInvalid(): boolean {
    return this.control.invalid || this.hasFormGroupErrors();
  }

  private hasFormGroupErrors(): boolean {
    const hasGroupErrors = this.formGroup.touched && this.formGroup.invalid && this.formGroupValidators?.length;

    return hasGroupErrors && this.formGroupValidators.some(x => this.formGroup.hasError(x));
  }

  getErrorMessage(): LabelWithParameters {
    if (!this.isControlInvalid()) {
      return null;
    }

    const errors = this.control.errors;

    if (errors) {
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        return this.getErrorLabel(errors, firstErrorKey);
      }
    }

    if (this.formGroup.errors && this.formGroupValidators?.length) {
      for (const key in Object.keys(this.formGroup.errors)) {
        if (this.formGroupValidators.indexOf(key) !== -1) {
          return this.getErrorLabel(this.formGroup.errors, key);
        }
      }
    }
  }

  private getErrorLabel(errors, key): LabelWithParameters {
    let errorLabel: LabelWithParameters;
    const params = errors[key];

    const error = this.errorMap[key];
    if (error) {
      const label = error.label;
      const value = error.value(params);

      errorLabel = {label, parameters: value};
    } else {
      errorLabel = {label: key, parameters: params};
    }

    return errorLabel;
  }

  hasRequiredValidator(): boolean {
    const validator = this.control?.validator;
    return typeof validator === 'function' && validator({} as AbstractControl)?.required;
  }

  getRequiredAsterix(): string {
    return this.hasRequiredValidator() && this.label ? ' *' : '';
  }

  registerOnTouched(fn: any): void {
    this.formControlDirective?.valueAccessor.registerOnTouched(fn);
  }

  registerOnChange(fn: any): void {
    this.formControlDirective?.valueAccessor.registerOnChange(fn);
  }

  writeValue(obj: any): void {
    this.formControlDirective?.valueAccessor.writeValue(obj);
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDirective?.valueAccessor.setDisabledState(isDisabled);
  }
}
